<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query';
import { GET_ACCOUNT_TEAM_MEMBERS_QUERY } from '@/services/account/queries/account';
import { UPDATE_USER_ROLE_IN_ACCOUNT, GRANT_USER_ROLES_TO_ACCOUNT } from '@/services/account/mutations/account';
import { GET_ENUMS_QUERY } from '@/services/common/queries.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { useUserStore } from '@/stores/user.store.js';
import Helpers from '@/helpers/index.js';
import HeaderPartial from '@/pages/partials/settings/headerPartial.vue';
import SectionPartial from '@/pages/partials/settings/sectionPartial.vue';
import BaseUserAvatar from '@/components/BaseUserAvatar.vue';

const queryClient = useQueryClient();
const accountPermissions = computed(() => useUserStore().currentPermissions.accounts);
const activeAccount = computed(() => useAccountStore().activeAccount);
const workspaces = computed(() => useWorkspaceStore().workspaces);
const teamMembers = ref([]);
const searchTeamMember = ref('');
const searchedWorkspace = ref([]);
const searchedRoles = ref([]);
const isUpdatingMemberRole = ref(false);
const isUpdatingMemberWorkspaces = ref(false);
const inviteTeamMemberDialog = ref(false);
const invitedTeamMembers = ref([]);
const selectedInvitedRole = ref(null);
const headers = [
  { title: 'Team Members', key: 'user', width: '150' },
  { title: 'Workspace', key: 'workspaces', width: '400' }
  // { title: 'Role', key: 'roles', width: '301' }
];

const menu = ref(false);
const message = ref(false);

watch(activeAccount, async (oldAccount, newAccount) => {
  await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
});

const { isLoading, isPending, isFetching, error, data } = useQuery({
  queryKey: ['loadAccountTeamMembers'],
  queryFn: async () => {
    const payload = {
      account_id: Number(activeAccount.value.id)
    };
    const res = await API.sendQuery('/graphql/account', GET_ACCOUNT_TEAM_MEMBERS_QUERY, payload);
    if (res.data.data.teamMembers) {
      teamMembers.value = res.data.data.teamMembers.map((member) => {
        const roles = member.workspaces.map((workspace) => {
          return workspace.role.name;
        });

        return {
          user: member.user,
          // roles: roles,
          // selectedRole: roles,
          workspaces: member.workspaces,
          // selectedWorkspaces: member.workspaces,
          editRoles: false,
          editWorkspaces: false
        };
      });
      return res.data.data.teamMembers;
    }
  },
  refetchOnWindowFocus: false
});

const {
  isLoading: isLoadingAccountRoles,
  isPending: isPendingAccountRoles,
  isFetching: isFetchingAccountRoles,
  data: accountRoles
} = useQuery({
  queryKey: ['loadAccountRoles'],
  queryFn: async () => {
    const payload = {
      name: 'ACLRoleEnum'
    };
    const res = await API.sendQuery('/graphql', GET_ENUMS_QUERY, payload);
    if (res.data.data.getEnums) {
      const visibleAccountRoleTypes = ['Owner', 'Admin', 'Editor', 'Accountant', 'Watcher'];
      return res.data.data.getEnums.filter((role) => {
        return visibleAccountRoleTypes.includes(role);
      });
    }
  },
  refetchOnWindowFocus: false
});

const filterBySearch = async (value) => {
  if (value === '' || value === null) {
    await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
  } else {
    teamMembers.value = teamMembers.value.filter((member) => {
      if (member.user?.first_name) {
        return member.user.first_name.toLowerCase().includes(value.toLowerCase());
      }

      if (member.user?.email) {
        return member.user.email.toLowerCase().includes(value.toLowerCase());
      }
    });
  }
};

const filterByWorkspace = async (value) => {
  if (value.length === 0) {
    await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
  } else {
    teamMembers.value = teamMembers.value.filter((member) => {
      return member.workspaces.some((item) => {
        return item.workspace ? value.includes(item.workspace.name) : false;
      });
    });
  }
};

const filterByRole = async (value) => {
  if (value.length === 0) {
    await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
  } else {
    teamMembers.value = teamMembers.value.filter((member) => {
      return member.roles.some((role) => {
        return value.includes(role);
      });
    });
  }
};

const updateUserRole = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/account', UPDATE_USER_ROLE_IN_ACCOUNT, payload);
  },
  onSuccess: (data, payload) => {
    if (data.data.data.updateUserRole) {
    }

    if (data.data.error) {
      console.log(data.data.error);
    }
  },
  onError: (error) => {
    console.log('error', error);
    isUpdatingMemberRole.value = false;
  }
});

const updateMemberRolesHandler = async (member) => {
  isUpdatingMemberRole.value = true;
  for (const role of member.selectedRole) {
    const payload = {
      account_id: Number(activeAccount.value.id),
      user_id: Number(member.user.id),
      role: role,
      member: member
    };
    await updateUserRole.mutateAsync(payload);
    const foundMemberIndex = teamMembers.value.indexOf(member);
    teamMembers.value[foundMemberIndex].roles = member.selectedRole;
    teamMembers.value[foundMemberIndex].editRoles = false;
    isUpdatingMemberRole.value = false;
  }
};

const updateMemberWorkspacesHandler = async (member) => {
  isUpdatingMemberWorkspaces.value = true;
  for (const workspace of member.selectedWorkspaces) {
    const payload = {
      account_id: Number(activeAccount.value.id),
      user_id: Number(member.user.id),
      workspace_id: Number(workspace.id),
      member: member
    };
    await updateUserRole.mutateAsync(payload);
    const foundMemberIndex = teamMembers.value.indexOf(member);
    teamMembers.value[foundMemberIndex].workspaces = member.selectedWorkspaces;
    teamMembers.value[foundMemberIndex].editWorkspaces = false;
    isUpdatingMemberWorkspaces.value = false;
  }
};
</script>

<template>
  <headerPartial class="tw-capitalize" :title="` ${activeAccount.name} Team Members`"></headerPartial>
  <SectionPartial :divider="false">
    <div class="tw-mb-5 tw-flex tw-items-center tw-space-x-3">
      <div class="tw-flex tw-min-w-[338px] tw-items-center">
        <v-text-field
          v-model="searchTeamMember"
          @update:modelValue="filterBySearch"
          hide-details
          density="compact"
          variant="solo"
          placeholder="Search for a team member"
          append-inner-icon="mdi-magnify"
          clearable
        ></v-text-field>
      </div>
      <div class="tw-flex tw-min-w-[173px] tw-items-center">
        <v-select
          :items="accountRoles"
          v-model="searchedRoles"
          @update:modelValue="filterByRole"
          density="compact"
          variant="solo"
          hide-details
          multiple
          clearable
          placeholder="Filter by role"
          style="height: auto !important"
        ></v-select>
      </div>
      <div class="tw-flex tw-min-w-[231px] tw-items-center">
        <v-select
          :items="workspaces"
          density="compact"
          variant="solo"
          @update:modelValue="filterByWorkspace"
          hide-details
          placeholder="Filter by workspace"
          item-title="name"
          multiple
          clearable
          style="height: auto !important"
        ></v-select>
      </div>
      <div class="tw-flex tw-items-center">
        <v-btn @click="inviteTeamMemberDialog = true" class="tw-text-[0.79rem] tw-capitalize" color="primary-blue" prepend-icon="mdi-plus">Invite Team Members</v-btn>

        <v-dialog v-model="inviteTeamMemberDialog" width="auto" attach="#app">
          <v-card class="tw-rounded-xl" max-width="702" width="702">
            <div class="tw-p-5">
              <h2 class="tw-text-center tw-text-2xl tw-font-bold tw-text-primary-blue">Invite Team Members</h2>
              <div class="tw-rounded-xl tw-bg-[#f7f7f7] tw-p-3">
                <v-form>
                  <div class="tw-flex tw-w-full tw-items-start tw-justify-between tw-space-x-3">
                    <div class="tw-flex tw-w-full tw-flex-col">
                      <v-combobox
                        width="472"
                        v-model="invitedTeamMembers"
                        color="primary-blue"
                        variant="solo"
                        density="compact"
                        :items="invitedTeamMembers"
                        label="Invite by email"
                        multiple
                      ></v-combobox>
                    </div>
                    <div class="tw-flex tw-w-full tw-flex-col">
                      <v-select min-width="141" width="141" v-model="selectedInvitedRole" :items="accountRoles" label="Select a role" color="primary-blue" variant="solo" density="compact"></v-select>
                    </div>
                  </div>

                  <!--                  <template v-slot:actions>-->
                  <!--                    <v-btn class="ms-auto" text="Ok" @click="inviteTeamMemberDialog = false"></v-btn>-->
                  <!--                  </template>-->
                </v-form>
              </div>
            </div>
          </v-card>
        </v-dialog>
      </div>
    </div>
    <v-data-table :loading="isFetching" :headers="headers" :items="teamMembers" item-key="user" loading-text="Loading Team Members..." :sticky="true" items-per-page="-1" hide-default-footer>
      <template v-slot:item.user="{ value }">
        <div class="tw-my-2 tw-flex tw-items-center tw-space-x-3">
          <BaseUserAvatar class="tw-mr-2" :text-styles="`cursor: pointer;font-size: 1.688rem; width: 80px; height: 80px; line-height: 64px;`" :size="64" :user="value" />
          <div class="tw-flex tw-flex-col tw-space-y-1">
            <template v-if="value.first_name || (value.username && value.username.length > 0)">
              <div class="tw-text tw-font-semibold">{{ value.username.length > 0 ? value.username : `${value.first_name} ${value.last_name}` }}</div>
            </template>
            <div class="tw-text-sm tw-font-medium tw-text-[#979797]">{{ value.email }}</div>
          </div>
        </div>
      </template>
      <!--      <template v-slot:item.roles="{ value, item }">-->
      <!--        <div class="tw-flex tw-items-center tw-space-x-5">-->
      <!--          <div class="tw-flex tw-items-center tw-space-x-2">-->
      <!--            <template v-for="(role, index) in value" :key="index">-->
      <!--              <v-chip size="small" :ripple="false" :color="Helpers.getRoleColor(role)">-->
      <!--                {{ role }}-->
      <!--              </v-chip>-->
      <!--            </template>-->
      <!--          </div>-->
      <!--        </div>-->
      <!--        <div class="tw-my-3">-->
      <!--          <v-menu attach="#app" v-model="item.editRoles" :close-on-content-click="false" location="end">-->
      <!--            <template v-slot:activator="{ props }">-->
      <!--              <v-btn v-bind="item.editRoles ? props : ''" density="compact" icon="mdi-plus-box" variant="text" @click="item.editRoles = true"></v-btn>-->
      <!--            </template>-->

      <!--            <v-card min-width="250">-->
      <!--              <div class="tw-mb-3 tw-p-2">-->
      <!--                <v-select v-model="item.selectedRole" item-title="name" chips variant="underlined" :disabled="isUpdatingMemberRole" label="Select roles" :items="accountRoles" multiple>-->
      <!--                  &lt;!&ndash;                    <template v-slot:item="{ props, item }">&ndash;&gt;-->
      <!--                  &lt;!&ndash;                      <v-list-item v-bind="props"></v-list-item>&ndash;&gt;-->
      <!--                  &lt;!&ndash;                    </template>&ndash;&gt;-->
      <!--                </v-select>-->
      <!--              </div>-->

      <!--              <v-card-actions>-->
      <!--                <v-spacer></v-spacer>-->

      <!--                <v-btn class="tw-capitalize" variant="tonal" size="small" :disabled="isUpdatingMemberRole" @click="item.editRoles = false"> Cancel </v-btn>-->
      <!--                <v-btn-->
      <!--                  class="tw-capitalize"-->
      <!--                  variant="flat"-->
      <!--                  size="small"-->
      <!--                  :disabled="isUpdatingMemberRole"-->
      <!--                  :loading="isUpdatingMemberRole"-->
      <!--                  color="secondary-blue"-->
      <!--                  @click="updateMemberRolesHandler(item)"-->
      <!--                >-->
      <!--                  Save-->
      <!--                </v-btn>-->
      <!--              </v-card-actions>-->
      <!--            </v-card>-->
      <!--          </v-menu>-->
      <!--        </div>-->
      <!--      </template>-->
      <template v-slot:item.workspaces="{ value, item }">
        <div class="tw-mt-3 tw-flex tw-space-x-5">
          <div class="tw-flex tw-flex-col tw-items-center tw-space-y-2">
            <template v-if="value[0].applies_to_all">
              <div class="tw-flex tw-items-center tw-space-x-2">
                <v-btn width="43" height="43" size="x-small" density="compact" color="primary-blue">
                  <v-icon class="tw-text-[1.8rem]" icon="mdi-earth"></v-icon>
                </v-btn>
                <div class="tw-font-semibold">ALL</div>
              </div>
            </template>
            <template v-if="value.workpaces" v-for="(workspace, index) in value.workpaces" :key="index">
              <div class="tw-flex tw-items-center tw-space-x-2">
                <v-btn width="43" height="43" size="x-small" density="compact" color="primary-blue">
                  <v-icon class="tw-text-[1.8rem]" icon="mdi-earth"></v-icon>
                </v-btn>
                <div class="tw-font-semibold">{{ value[0].applies_to_all ? 'ALL' : workspace.workspace.name }}</div>
              </div>
            </template>
          </div>
        </div>
        <div class="tw-my-3">
          <!--          <v-menu attach="#app" v-model="item.editWorkspaces" :close-on-content-click="false" location="end">-->
          <!--            <template v-slot:activator="{ props }">-->
          <!--              <v-btn v-bind="item.editWorkspaces ? props : ''" @click="item.editWorkspaces = true" density="compact" icon="mdi-plus-box" variant="text"></v-btn>-->
          <!--            </template>-->

          <!--            <v-card min-width="250">-->
          <!--              <div class="tw-mb-3 tw-p-2">-->
          <!--                <v-select-->
          <!--                  v-model="item.selectedWorkspaces"-->
          <!--                  item-title="name"-->
          <!--                  chips-->
          <!--                  variant="underlined"-->
          <!--                  return-object-->
          <!--                  :disabled="isUpdatingMemberWorkspaces"-->
          <!--                  label="Select workspaces"-->
          <!--                  :items="workspaces"-->
          <!--                  multiple-->
          <!--                >-->
          <!--                </v-select>-->
          <!--              </div>-->

          <!--              <v-card-actions>-->
          <!--                <v-spacer></v-spacer>-->

          <!--                <v-btn class="tw-capitalize" variant="tonal" size="small" :disabled="isUpdatingMemberWorkspaces" @click="item.editWorkspaces = false"> Cancel </v-btn>-->
          <!--                <v-btn-->
          <!--                  class="tw-capitalize"-->
          <!--                  variant="flat"-->
          <!--                  size="small"-->
          <!--                  :disabled="isUpdatingMemberWorkspaces"-->
          <!--                  :loading="isUpdatingMemberWorkspaces"-->
          <!--                  color="secondary-blue"-->
          <!--                  @click="updateMemberWorkspacesHandler(item)"-->
          <!--                >-->
          <!--                  Save-->
          <!--                </v-btn>-->
          <!--              </v-card-actions>-->
          <!--            </v-card>-->
          <!--          </v-menu>-->
        </div>
      </template>
    </v-data-table>
  </SectionPartial>
</template>

<style scoped></style>
